/**
 * IDB Utilities
 *
 * Shared low-level helpers for IndexedDB operations.
 * Import from here instead of duplicating promise wrappers.
 */

/**
 * Global registry of IDB database names that are currently open via an
 * active IndexeddbPersistence instance.
 *
 * Eviction code (idbManifest.evictLRU / evictByAge) checks this set before
 * deleting a database, skipping any that are currently in use. This prevents
 * the "NotFoundError: One of the specified object stores was not found" error
 * that occurs when y-indexeddb tries to use a connection to a database that
 * was deleted while it was still open.
 */
const _openIDBKeys = new Set<string>();

export function registerOpenIDB(key: string): void {
  _openIDBKeys.add(key);
}

export function unregisterOpenIDB(key: string): void {
  _openIDBKeys.delete(key);
}

export function isIDBOpen(key: string): boolean {
  return _openIDBKeys.has(key);
}

/**
 * Delete an IndexedDB database by name.
 * Resolves (never rejects) regardless of success, error, or blocked state,
 * since callers generally can't recover from a failed delete.
 */
export function deleteIDBDatabase(name: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}
